import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface PerformanceData {
  sector: string;
  sector_symbol: string;
  change_percentage: number;
  last_updated_at: string;
}

export class TraderverseMarketSectorPerformance {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private lastFetchedAt: Date | null = null;

    private widgetBoxEl: HTMLElement | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    this.attachStyles();
    this.widgetBoxEl = this.shadowRoot.querySelector('.widgetBox');
    if (this.widgetBoxEl) {
      this.widgetBoxEl.classList.add('loading');
    }
    this.emit('ready', {});
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
            <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Sector Performance</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
          </div>
          <div class="inBody">

            <div class="chartDv" id="sectorPerformanceContainer">
            </div>

          </div>
        </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    this.loadPerformanceData();
  }

  


 

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }


  private async loadPerformanceData(interval: string = '1w'): Promise<void> {
    try {
      const performanceData = await this.fetchPerformanceData(this.config.symbol!,interval);
      const fromApi = (performanceData?.[0] as any)?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();
      this.renderPerformanceData(performanceData);
    } catch (error) {
      console.error('Failed to load performance data:', error);
      this.renderError();
    }
  }

  private async fetchPerformanceData(symbol:string,interval: string): Promise<PerformanceData[]> {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + 4);

    const formatDate = (date: Date) => date.toISOString().slice(0, 10);
    return new Promise((resolve) => {

      apiGet({
        method: "POST",
        url: `${APIURL}/v3/discover/sector_performance`,
        body: {
          start_date: formatDate(startDate),
          end_date: formatDate(endDate),
          limit: 11
        }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      }).then((responseData) => {
        const performanceData: PerformanceData[] = responseData.data.map((item: any) => ({
          sector: item.sector,
          sector_symbol: item.sector_symbol,
          change_percentage: item.change_percentage,
          last_updated_at: item.last_updated_at
        }));
        resolve(performanceData);
      });
    });
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private renderPerformanceData(data: PerformanceData[]): void {
    this.updateLastUpdatedTooltip();
    const container = this.shadowRoot.querySelector('#sectorPerformanceContainer');
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;

    if (!container) return;
    if (!Array.isArray(data) || data.length === 0) {
    chartDv.removeAttribute('data-testing-id');
    container.innerHTML = getNoDataHTML();
    return;
  }

  // Data aa gaya => attribute add karo
  chartDv.setAttribute(
    'data-testing-id',
    'market-sector-performance-content'
  );

    // Remove loading class when data is loaded
    if (this.widgetBoxEl) {
      this.widgetBoxEl.classList.remove('loading');
    }

    // Find the maximum absolute percentage to normalize the bar widths
    const maxAbsPercentage = Math.max(...data.map(item => Math.abs(item.change_percentage)));

    const html = `
  <div class="marketSectorPerformanceMain">
    <div class="headItem">
      <p>Sector</p>
      <p>Change%</p>
    </div>
    ${data.map(item => {
      // Normalize width to fill half the container (0-50%) since bars start from center
      const normalizedWidth = maxAbsPercentage > 0 ? Math.min((Math.abs(item.change_percentage) / maxAbsPercentage) * 50, 50) : 0;
      return `
      <div class="item">
        <p>${item.sector}</p>
        <div class="rangeStat ${item.change_percentage >= 0 ? 'green' : 'red'}">
          <span><span style="width:${normalizedWidth}%;"></span></span>
        </div>
        <p>${item.change_percentage.toFixed(2)}%</p>
      </div>
    `;
    }).join('')}
  </div>
`;

    container.innerHTML = html;
  }

  private getTimeAgo(dateString: string): string {
    const now = new Date();
    const publishDate = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - publishDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Less than an hour ago';
    } else if (diffInHours === 1) {
      return '1 hour ago';
    } else {
      return `${diffInHours} hours ago`;
    }
  }

  private renderError(): void {
    const container = this.shadowRoot.querySelector('.news-highlights-dv');
    if (!container) return;

    container.innerHTML = '<div class="error">Failed to load news data</div>';
  }

  private async saveAsImage(): Promise<void> {
    try {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (!widget || !(window as any).html2canvas) return;

      // Temporarily clone the widget to regular DOM for html2canvas
      const clone = widget.cloneNode(true) as HTMLElement;

      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = widget.offsetWidth + 'px';
      tempContainer.style.height = widget.offsetHeight + 'px';

      // Copy computed styles
      const widgetStyles = window.getComputedStyle(widget);
      for (let i = 0; i < widgetStyles.length; i++) {
        const property = widgetStyles[i];
        clone.style.setProperty(property, widgetStyles.getPropertyValue(property));
      }

      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);

      // Capture the image
      const canvas = await (window as any).html2canvas(clone, {
        backgroundColor: null,
        scale: 2,
        useCORS: true
      });

      // Clean up
      document.body.removeChild(tempContainer);

      // Convert to blob and emit
      canvas.toBlob((blob: Blob) => {
        if (blob) {
          this.emit('ready' as any, { blob, canvas });

          // Optional: Auto-download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `traderverse-news-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');

    } catch (error) {
      console.error('Failed to capture image:', error);
      this.emit('ready' as any, { error });
    }
  }

  private toggleTheme(): void {
    const widget = this.shadowRoot.querySelector('.widgetBox');
    if (widget) {
      const currentTheme = widget.getAttribute('data-theme');
      const newTheme = currentTheme === 'black' ? 'white' : 'black';
      widget.setAttribute('data-theme', newTheme);
      this.config.theme = newTheme as 'white' | 'black';
    }
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.container.requestFullscreen?.();
    }
  }

  // Public API methods
  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }



  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    // this.updateDisplay();
  }

  public refresh(): void {
    this.loadPerformanceData();
  }

  public destroy(): void {
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach(handler => handler(event));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

// Global registration for script tag usage
declare global {
  interface Window {
    TraderverseMarketSectorPerformance: typeof TraderverseMarketSectorPerformance;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseMarketSectorPerformance = TraderverseMarketSectorPerformance;
}
